import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "../../lib/utils"

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col gap-2 w-fit", className)}
      {...props}
    />
  )
}

function TabsList({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & {
  variant?: "default" | "compact"
}) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        "bg-background-tabs-gray text-muted-foreground inline-flex h-10 w-fit items-center rounded-lg p-0.5",
        variant === "compact" && "flex-wrap h-auto gap-1 w-auto max-w-full overflow-x-auto",
        className
      )}
      {...props}
    />
  )
}

interface TabsTriggerProps extends React.ComponentProps<typeof TabsPrimitive.Trigger> {
  variant?: "default" | "compact"
}

function TabsTrigger({
  className,
  variant = "default",
  ...props
}: TabsTriggerProps) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "text-foreground inline-flex h-full min-h-8 items-center justify-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium whitespace-nowrap transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
        "data-[state=active]:bg-[#0096b5] data-[state=active]:text-white",
        variant === "compact" && "px-2.5 py-1.5 text-xs",
        className
      )}
      {...props}
    />
  )
}

interface TabsTriggerWithNumberProps extends React.ComponentProps<typeof TabsPrimitive.Trigger> {
  number: number
  variant?: "default" | "compact"
}

function TabsTriggerWithNumber({
  className,
  number,
  variant = "default",
  ...props
}: TabsTriggerWithNumberProps) {
  return (
    <TabsPrimitive.Trigger
      {...props}
      className={cn(
        "group relative flex items-center gap-2",
        "text-foreground inline-flex h-full items-center justify-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium whitespace-nowrap transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
        "data-[state=active]:bg-[#0096b5] data-[state=active]:text-white",
        variant === "compact" && "px-2.5 py-1.5 text-xs gap-1",
        className
      )}
    >
      {props.children}
      
      {number > 0 &&(
        <div className={cn(
          "flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full transition-colors",
          "group-data-[state=active]:bg-[#1DD0F4] group-data-[state=active]:text-primary-foreground group-data-[state=active]:border group-data-[state=active]:border-[#01A9CE]",
          "bg-orange-50 border border-orange-200 text-foreground",
          variant === "compact" && "w-5 h-5 text-[10px]"
        )}>
          {number}
        </div>
      )}
    </TabsPrimitive.Trigger>
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsTriggerWithNumber, TabsContent }
